/**
 * Difficulty model.
 *
 * Faithful to Good Sudoku's design philosophy: difficulty is defined by the
 * solving *techniques* a puzzle requires, not by clue count. The five levels
 * map onto the five technique tiers in techniques.ts. A puzzle's grade is the
 * hardest tier needed to crack it with pure logic.
 */

import { Grid } from "./board.js";
import {
  TECHNIQUE_NAME,
  TECHNIQUE_TIER,
  TechniqueId,
  solveLogically,
} from "./techniques.js";

export type DifficultyId = "easy" | "medium" | "hard" | "expert" | "insane";

export interface DifficultyDef {
  id: DifficultyId;
  label: string;
  /** Technique tier this level tops out at. */
  tier: number;
  stars: number;
  /** One-line description shown when choosing a level. */
  blurb: string;
  /** Techniques newly required at this level (for the "what you'll need" UI). */
  techniquesIntroduced: TechniqueId[];
}

// Difficulty is rebalanced so even Easy demands a real technique beyond plain
// singles, no busywork. Each level requires (at least) the techniques of its
// tier, so the puzzles actively push you to learn new skills.
export const DIFFICULTIES: readonly DifficultyDef[] = [
  {
    id: "easy",
    label: "Easy",
    tier: 2,
    stars: 1,
    blurb: "Pointing pairs and locked candidates, real deduction from the start.",
    techniquesIntroduced: ["pointing", "claiming", "nakedPair", "hiddenPair"],
  },
  {
    id: "medium",
    label: "Medium",
    tier: 3,
    stars: 2,
    blurb: "Triples, quads and your first X-Wing.",
    techniquesIntroduced: ["nakedTriple", "hiddenTriple", "nakedQuad", "hiddenQuad", "xWing"],
  },
  {
    id: "hard",
    label: "Hard",
    tier: 4,
    stars: 3,
    blurb: "Swordfish, kites and wings. Disciplined candidate work required.",
    techniquesIntroduced: ["swordfish", "skyscraper", "twoStringKite", "xyWing", "xyzWing"],
  },
  {
    id: "expert",
    label: "Expert",
    tier: 4,
    stars: 4,
    blurb: "Fish, wings and uniqueness tricks at full stretch.",
    techniquesIntroduced: ["wWing", "uniqueRectangle", "swordfish", "jellyfish"],
  },
  {
    id: "insane",
    label: "Insane",
    tier: 5,
    stars: 5,
    blurb: "Jellyfish and the nastiest patterns. No mercy.",
    techniquesIntroduced: ["jellyfish"],
  },
];

export const DIFFICULTY_BY_ID: Record<DifficultyId, DifficultyDef> = Object.fromEntries(
  DIFFICULTIES.map((d) => [d.id, d]),
) as Record<DifficultyId, DifficultyDef>;

const TIER_TO_ID: Record<number, DifficultyId> = {
  0: "easy",
  1: "easy",
  2: "easy",
  3: "medium",
  4: "hard",
  5: "insane",
};

export function tierToDifficulty(tier: number): DifficultyId {
  return TIER_TO_ID[tier] ?? "insane";
}

export interface GradeResult {
  /** True if the puzzle can be solved by the implemented technique set. */
  solvableByLogic: boolean;
  /** Hardest technique tier required (0 if trivially complete). */
  tier: number;
  difficulty: DifficultyId;
  /** How many times each technique fired during the logical solve. */
  techniqueCounts: Record<string, number>;
  /** Distinct technique names used, hardest last. */
  techniquesUsed: string[];
}

/**
 * Grade a puzzle by logically solving it and recording the hardest technique
 * required. If the implemented techniques can't finish it, `solvableByLogic`
 * is false (the puzzle needs deductions beyond this engine, e.g. chains).
 */
export function gradeGrid(grid: Grid): GradeResult {
  const result = solveLogically(grid);
  const counts: Record<string, number> = {};
  for (const [id, n] of result.usedTechniques) counts[id] = n;
  const used = [...result.usedTechniques.keys()]
    .sort((a, b) => TECHNIQUE_TIER[a] - TECHNIQUE_TIER[b])
    .map((id) => TECHNIQUE_NAME[id]);

  return {
    solvableByLogic: result.solved,
    tier: result.maxTier,
    difficulty: tierToDifficulty(result.maxTier),
    techniqueCounts: counts,
    techniquesUsed: used,
  };
}
